import { keys } from '../keys';
import {
  createBillingConfig,
  PriceInterval,
  PriceModel,
  PriceType
} from './schema';

export enum Feature {
  ContactManagement = 'Bedrijven en contacten',
  Activities = 'Activiteiten',
  Tasks = 'Taken',
  Notes = 'Notities',
  Sales = 'Sales pipeline',
  Quotes = 'Offertes',
  Invoicing = 'Facturatie',
  RevenueHub = 'Revenue Hub'
}

const currency = 'EUR';

export const billingConfig = createBillingConfig({
  products: [
    {
      id: 'free',
      name: 'Free',
      description: 'Gratis proefaccount',
      label: 'Start nu',
      isFree: true,
      hidden: true,
      features: [
        Feature.ContactManagement,
        Feature.Activities,
        Feature.Tasks,
        Feature.Notes,
        Feature.Sales,
        Feature.Quotes,
        Feature.Invoicing,
        Feature.RevenueHub
      ],
      // Even if it is free, keep the plans and prices to display the interval and currency correctly
      plans: [
        {
          id: 'plan-free-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price-free-month-id', // a placebo ID is fine here
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 0,
              currency
            }
          ]
        },
        {
          id: 'plan-free-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id: 'price-free-year-id', // a placebo ID is fine here
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 0,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'plan-secretary',
      name: 'Secretaresse',
      description: 'Voor simpele ondersteuning.',
      label: 'Start nu',
      recommended: false,
      features: [
        Feature.ContactManagement,
        Feature.Activities,
        Feature.Tasks,
        Feature.Notes
      ],
      plans: [
        {
          id: 'plan-secretary-month',
          displayIntervals: [PriceInterval.Month],
          trialDays: 10,
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_SMALL_MONTH_ID ||
                'price-pro-month-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.PerSeat,
              cost: 15,
              currency
            }
          ]
        },
        {
          id: 'plan-secretary-year',
          displayIntervals: [PriceInterval.Year],
          trialDays: 10,
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_SMALL_YEAR_ID ||
                'price-pro-year-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.PerSeat,
              cost: 150,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'plan-office-manager',
      name: 'Office Manager',
      description: 'Gevorderde tools en integraties.',
      label: 'Start nu',
      recommended: true,
      features: [
        Feature.ContactManagement,
        Feature.Activities,
        Feature.Tasks,
        Feature.Notes,
        Feature.Sales,
        Feature.Quotes,
        Feature.Invoicing
      ],
      plans: [
        {
          id: 'plan-office-manager-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_MEDIUM_MONTH_ID ||
                'price-pro-month-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.PerSeat,
              cost: 25,
              currency
            }
          ]
        },
        {
          id: 'plan-office-manager-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_MEDIUM_YEAR_ID ||
                'price-pro-year-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.PerSeat,
              cost: 250,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'plan-management-assistant',
      name: 'Directie assistente',
      description: 'Alles erop en eraan.',
      label: 'Start nu',
      recommended: false,
      features: [
        Feature.ContactManagement,
        Feature.Activities,
        Feature.Tasks,
        Feature.Notes,
        Feature.Sales,
        Feature.Quotes,
        Feature.Invoicing,
        Feature.RevenueHub
      ],
      plans: [
        {
          id: 'plan-management-assistant-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_LARGE_MONTH_ID ||
                'price-pro-month-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.PerSeat,
              cost: 35,
              currency
            }
          ]
        },
        {
          id: 'plan-management-assistant-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_LARGE_YEAR_ID ||
                'price-pro-year-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.PerSeat,
              cost: 350,
              currency
            }
          ]
        }
      ]
    }
  ]
});

export const billingConfigDisplayIntervals = Array.from(
  new Set(
    billingConfig.products
      .filter((product) => !product.hidden)
      .flatMap((product) =>
        product.plans.flatMap((plan) => plan.displayIntervals)
      )
      .filter(Boolean)
  )
) as PriceInterval[];
